import { addDaysLocal, parseLocalIsoDate } from "@/lib/bookingDates";
import { getGermanPublicHoliday } from "@/lib/germanHolidays";

export const GANTT_DAY_WIDTH_PX = 40;
export const GANTT_ROW_HEIGHT_PX = 46;
export const GANTT_LABEL_WIDTH_PX = 188;
export const GANTT_INITIAL_WEEKS = 8;
export const GANTT_LOAD_MORE_WEEKS = 4;
export const GANTT_WEEK_DAYS = 7;
export const GANTT_SCROLL_EDGE_PX = 120;

const WEEKDAY_SHORT = new Intl.DateTimeFormat("de-DE", { weekday: "short" });
const MONTH_SHORT = new Intl.DateTimeFormat("de-DE", { month: "short" });

export interface GanttDay {
  iso: string;
  dayOfMonth: number;
  weekday: string;
  isWeekend: boolean;
  isHoliday: boolean;
  isToday: boolean;
  holidayName: string | null;
  weekKey: string;
  monthKey: string;
}

export interface GanttBar {
  id: string;
  from: string;
  to: string;
  label: string;
  sublabel?: string;
  kind: "occupied" | "free" | "blocked_pitch" | "custom" | "holiday";
  color?: string;
  isFixed?: boolean;
  bookingId?: string;
  customEntryId?: string;
}

export function buildGanttDays(
  rangeFrom: string,
  rangeToInclusive: string,
  todayIso?: string,
  holidayNames?: ReadonlyMap<string, string> | null,
): GanttDay[] {
  const today = todayIso ?? "";
  const days: GanttDay[] = [];
  let current = rangeFrom;
  while (current <= rangeToInclusive) {
    const date = parseLocalIsoDate(current);
    const weekdayIndex = date.getDay();
    const weekKey = isoWeekKey(current);
    const holidayName = holidayNames
      ? (holidayNames.get(current) ?? null)
      : getGermanPublicHoliday(current);
    days.push({
      iso: current,
      dayOfMonth: date.getDate(),
      weekday: WEEKDAY_SHORT.format(date),
      isWeekend: weekdayIndex === 0 || weekdayIndex === 6,
      isHoliday: holidayName != null,
      isToday: today !== "" && current === today,
      holidayName,
      weekKey,
      monthKey: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
    });
    current = addDaysLocal(current, 1);
  }
  return days;
}

function isoWeekKey(isoDate: string): string {
  const date = parseLocalIsoDate(isoDate);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day + 3);
  const firstThursday = date.getTime();
  date.setMonth(0, 1);
  if (date.getDay() !== 4) {
    date.setMonth(0, 1 + ((4 - date.getDay() + 7) % 7));
  }
  const week = 1 + Math.round((firstThursday - date.getTime()) / 604_800_000);
  return `${date.getFullYear()}-KW${String(week).padStart(2, "0")}`;
}

export function dayIndex(days: GanttDay[], isoDate: string): number {
  return days.findIndex((day) => day.iso === isoDate);
}

/** Position für Halboffenes Intervall [from, to). */
export function barStyle(
  days: GanttDay[],
  from: string,
  to: string,
): { left: number; width: number } | null {
  if (days.length === 0) return null;
  const rangeStart = days[0]!.iso;
  const rangeEndExclusive = addDaysLocal(days[days.length - 1]!.iso, 1);
  const clippedFrom = from < rangeStart ? rangeStart : from;
  const clippedTo = to > rangeEndExclusive ? rangeEndExclusive : to;
  if (clippedFrom >= clippedTo) return null;

  const startIndex = Math.max(0, dayIndex(days, clippedFrom));
  let endIndex = dayIndex(days, clippedTo);
  if (endIndex < 0) {
    endIndex = days.length;
  }
  const widthDays = Math.max(endIndex - startIndex, clippedTo > days[days.length - 1]!.iso ? 1 : 0);
  if (widthDays <= 0 && clippedTo <= rangeEndExclusive) {
    const fallbackEnd = dayIndex(days, addDaysLocal(clippedFrom, 1));
    const end = fallbackEnd >= 0 ? fallbackEnd : startIndex + 1;
    return {
      left: startIndex * GANTT_DAY_WIDTH_PX,
      width: Math.max((end - startIndex) * GANTT_DAY_WIDTH_PX, GANTT_DAY_WIDTH_PX * 0.6),
    };
  }
  return {
    left: startIndex * GANTT_DAY_WIDTH_PX,
    width: Math.max(widthDays * GANTT_DAY_WIDTH_PX, GANTT_DAY_WIDTH_PX * 0.6),
  };
}

export function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year ?? 0, (month ?? 1) - 1, 1);
  return `${MONTH_SHORT.format(date)} ${year}`;
}
